import { useEffect, useMemo } from 'react';
import { fg } from '@atlassian/jira-feature-gating';

import type { Person } from '@atlassian/jira-filters/src/common/types.tsx';
import { getUserShape } from '@atlassian/jira-issue-field-assignee/src/common/utils.tsx';
import { useFieldValue } from '@atlassian/jira-issue-field-base/src/services/field-value-service/index.tsx';
import { ASSIGNEE_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { useDiffCheck } from '../../../../common/hooks/use-diff-check/index.tsx';
import { SYNC } from '../../../../model/issue/issue-update-origin.tsx';
import { cardDataSet } from '../../../../state/actions/card/index.tsx';
import { useBoardActionCreator, useBoardSelector } from '../../../../state/index.tsx';
import { getPerson } from '../../../../state/selectors/people/people-selectors.tsx';
import { useIsIncrementPlanningBoard } from '../../../../state/state-hooks/capabilities/index.tsx';
import type { FieldSyncAssigneeProps } from './types.tsx';

export const FieldSyncAssignee = ({ issue, projectKey }: FieldSyncAssigneeProps) => {
	const { key, assigneeAccountId, id } = issue;

	const assigneeIdFromBoard = useMemo(
		() => (assigneeAccountId ? String(assigneeAccountId) : ''),
		[assigneeAccountId],
	);

	const assigneeFromBoard = useBoardSelector((state) => getPerson(state, assigneeIdFromBoard));
	const setAssigneeInBoard = useBoardActionCreator((assignee: Person | null) =>
		cardDataSet({ ...issue, assigneeAccountId: assignee?.id ?? null }, assignee, SYNC),
	);
	const isIPBoard = useIsIncrementPlanningBoard();
	// In the increment planning board, scenario issue keys do not yet have a numeric identifier as
	// they have not been created in Jira yet. In this case, the key is equal to the projectKey,
	// and thus not unique. Instead, we pass the issue id to the field config service (as it
	// requires a unique identifier for each issue).
	const uniqueKey =
		isIPBoard && key === projectKey && fg('issue_cards_in_program_board') ? `${id}` : key;
	const [assigneeFromIssueField, { setFieldValue: setAssigneeInIssueField }] = useFieldValue({
		issueKey: uniqueKey,
		fieldKey: ASSIGNEE_TYPE,
	});

	const assigneeIdFromIssueField = useMemo(
		() => assigneeFromIssueField?.accountId || null,
		[assigneeFromIssueField?.accountId],
	);

	const assigneeFromBoardChanged = useDiffCheck([assigneeIdFromBoard]);
	const assigneeFromIssueFieldChanged = useDiffCheck([assigneeIdFromIssueField]);

	const storesNeedToSync = assigneeIdFromBoard !== assigneeIdFromIssueField;

	// Board -> Issue Field Sync
	useEffect(() => {
		if (assigneeFromBoardChanged && storesNeedToSync) {
			setAssigneeInIssueField(
				uniqueKey,
				ASSIGNEE_TYPE,
				assigneeFromBoard
					? getUserShape(
							assigneeFromBoard.id,
							assigneeFromBoard.displayName,
							assigneeFromBoard.avatarUrl || '',
						)
					: null,
			);
		}
	}, [
		uniqueKey,
		assigneeFromBoardChanged,
		storesNeedToSync,
		setAssigneeInIssueField,
		assigneeFromBoard,
	]);

	// Issue Field -> Board Sync
	useEffect(() => {
		if (!assigneeFromBoardChanged && assigneeFromIssueFieldChanged && storesNeedToSync) {
			const issueFieldPerson: Person = assigneeFromIssueField
				? {
						...assigneeFromIssueField,
						avatarUrl: assigneeFromIssueField?.avatarUrls?.['48x48'],
						id: assigneeFromIssueField.accountId,
					}
				: null;
			setAssigneeInBoard(issueFieldPerson);
		}
	}, [
		assigneeFromBoardChanged,
		assigneeFromIssueField,
		assigneeFromIssueFieldChanged,
		setAssigneeInBoard,
		storesNeedToSync,
	]);

	return null;
};
